import React, { useState } from 'react';
import { Tag, message, Button, Modal, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { useRemoveGovernanceFlagsAspectMutation } from '../../../../../../../graphql/mutations.generated';
import { AddOrRemoveGovernanceFlagModal } from '../../../../EntityDropdown/AddOrRemoveGovernanceFlagModal';
import { Maybe, GovernanceFlagDetails } from '../../../../../../../types.generated';
import { useTranslation } from '../../../../../../../i18n/useTranslation';

const GovernanceFlagContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 4px;
`;

const NoElementButton = styled(Button)`
    padding: 0 4px;
    height: 22px;
    font-size: 12px;
`;

type Props = {
    editableFlags?: Maybe<Maybe<GovernanceFlagDetails>[]> | null;
    uneditableFlags?: Maybe<Maybe<GovernanceFlagDetails>[]> | null;
    fieldPath: string;
    entityUrn: string;
    entityData: any;
    canAddFlag: boolean;
    refetch?: () => void;
    readOnly?: boolean;
    /** When set, used as tooltip when the control is disabled (e.g. RBAC permission message). */
    disabledTooltip?: string;
};

export default function GovernanceFlagGroup({
    editableFlags,
    uneditableFlags,
    fieldPath,
    entityUrn,
    entityData,
    canAddFlag,
    refetch,
    readOnly,
    disabledTooltip,
}: Props) {
    const { t } = useTranslation();
    const [showAddModal, setShowAddModal] = useState(false);
    const [removeGovernanceFlag] = useRemoveGovernanceFlagsAspectMutation();

    const removeGovernanceFlagMutation = (flag) => {
        return removeGovernanceFlag({
            variables: {
                urn: entityUrn,
                input: {
                    name: flag.name,
                    subResource: fieldPath,
                },
            },
        })
            .then(({ errors }) => {
                if (!errors) {
                    message.success({ content: t('Flag removed successfully'), duration: 2 });
                }
            })
            .then(refetch)
            .catch((e) => {
                message.destroy();
                message.error({
                    content: t('Failed to remove flag: {{message}}', { message: e.message || '' }),
                    duration: 3,
                });
            });
    };

    // Function to remove the governance flag for a field in the schema table
    const removeFlag = (flag) => {
        Modal.confirm({
            title: t('Do you want to remove {{flagName}} flag?', { flagName: flag.name }),
            content: t('Are you sure you want to remove the {{flagName}} flag?', { flagName: flag.name }),
            onOk() {
                removeGovernanceFlagMutation(flag);
            },
            onCancel() {},
            okText: t('Yes'),
            cancelText: t('Cancel'),
            className: 'f-borderless-modal',
            okButtonProps: {
                type: undefined,
                loading: false,
                danger: true,
            },
            autoFocusButton: null,
            maskClosable: true,
            centered: true,
        });
    };

    return (
        <GovernanceFlagContainer>
            {editableFlags?.map((flag, index) => {
                if (!flag) return null;
                return (
                    <Tag
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${flag?.name}-${index}`}
                        closable={!readOnly}
                        className="pri-tag"
                        onClose={(e) => {
                            e.preventDefault();
                            if (!readOnly) {
                                removeFlag(flag);
                            }
                        }}
                    >
                        {flag?.name || ''}
                    </Tag>
                );
            })}

            {uneditableFlags?.map((flag, index) => {
                if (!flag) return null;
                return (
                    <Tag
                        // eslint-disable-next-line react/no-array-index-key
                        key={`${flag?.name}-${index}`}
                        className="pri-tag"
                    >
                        {flag?.name || ''}
                    </Tag>
                );
            })}

            {canAddFlag && (
                <Tooltip
                    title={disabledTooltip ?? (readOnly ? t('ASSET_MARKED_AS_READ_ONLY') : undefined)}
                    overlayClassName="f-tooltip-white"
                >
                    <NoElementButton
                        type="link"
                        onClick={() => setShowAddModal(true)}
                        icon={<PlusOutlined />}
                        aria-label="Add Flag"
                        disabled={readOnly}
                    >
                        {t('Add Flag')}
                    </NoElementButton>
                </Tooltip>
            )}

            {showAddModal && entityData && (
                <AddOrRemoveGovernanceFlagModal
                    entityData={entityData}
                    field={{ fieldPath }}
                    refetch={refetch}
                    onClose={() => setShowAddModal(false)}
                />
            )}
        </GovernanceFlagContainer>
    );
}
